import React, { useEffect, useState } from 'react';
import { Users, UserCheck, Key, AlertTriangle, TrendingUp, Activity } from 'lucide-react';
import { adminAPI } from '../../utils/api';
import StatsCard from './StatsCard';
import GrowthPanel from './GrowthPanel';
import OpsStatusPanel from './OpsStatusPanel';

interface DashboardStats {
  users: {
    total: number;
    verified: number;
    new30d: number;
    growth: number;
  };
  posts: {
    total: number;
    today: number;
  };
  apiKeys: {
    total: number;
    active: number;
  };
  reports: {
    pending: number;
    resolved30d: number;
  };
  activity: Array<{
    date: string;
    newUsers: number;
    newPosts: number;
  }>;
}

const DashboardOverview: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getDashboardStats();
        setStats(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.error || '통계를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* 통계 카드 */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">📊 대시보드 개요</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="총 사용자"
            value={stats.users.total}
            change={stats.users.growth}
            icon={<Users className="w-6 h-6" />}
            color="blue"
          />
          <StatsCard
            title="검증된 의사"
            value={stats.users.verified}
            icon={<UserCheck className="w-6 h-6" />}
            color="green"
          />
          <StatsCard
            title="활성 API 키"
            value={stats.apiKeys.active}
            icon={<Key className="w-6 h-6" />}
            color="yellow"
          />
          <StatsCard
            title="대기중 신고"
            value={stats.reports.pending}
            icon={<AlertTriangle className="w-6 h-6" />}
            color="red"
          />
        </div>
      </div>

      {/* North Star 성장 지표 — 활성 사용자 · 에이전트 참조 */}
      <GrowthPanel />

      {/* 운영 상태 — 외부 연동 여부와 탈퇴 통계 */}
      <OpsStatusPanel />

      {/* 최근 활동 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-gray-400" />
          🔥 최근 활동
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600">오늘의 신규 가입</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {stats.users.new30d > 0 ? Math.ceil(stats.users.new30d / 30) : 0}명
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">오늘의 게시글</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.posts.today}개</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">총 게시글</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.posts.total}개</p>
          </div>
        </div>
      </div>

      {/* 주의 필요 항목 */}
      {(stats.reports.pending > 0 || stats.users.new30d > 10) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            ⚠️ 주의 필요
          </h3>
          <ul className="space-y-2">
            {stats.reports.pending > 0 && (
              <li className="text-yellow-800">
                • 처리 대기 중인 신고: <span className="font-bold">{stats.reports.pending}건</span>
              </li>
            )}
            {stats.users.new30d > 10 && (
              <li className="text-yellow-800">
                • 최근 30일 신규 가입: <span className="font-bold">{stats.users.new30d}명</span> (검증 필요)
              </li>
            )}
            {stats.apiKeys.total - stats.apiKeys.active > 0 && (
              <li className="text-yellow-800">
                • 비활성 API 키: <span className="font-bold">{stats.apiKeys.total - stats.apiKeys.active}개</span>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* 활동 추이 (간단한 테이블 형태) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-gray-400" />
          📈 최근 7일 활동 추이
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  날짜
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  신규 가입
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  신규 게시글
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.activity.slice(-7).map((day, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {new Date(day.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {day.newUsers}명
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {day.newPosts}개
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;

